import { useState } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import { OperationVerificationDocumentField } from '@/components/venue/OperationVerificationDocumentField'
import { useSubmitVenueBusinessInfoChangeRequest } from '@/hooks/useSubmitVenueBusinessInfoChangeRequest'
import { useVenueBusinessInfo } from '@/hooks/useVenueBusinessInfo'
import { toApiError } from '@/lib/api/errors'
import type { VenueBusinessInfoView } from '@/lib/venue/venueBusinessInfoTypes'
import {
  emptyVenueBusinessInfoFormState,
  hasPendingBusinessInfoChangeRequest,
  isVenueBusinessInfoFormComplete,
  toVenueBusinessInfoChangeRequestInput,
  validateVenueBusinessInfoForm,
  VENUE_BUSINESS_INFO_LIMITS,
  type VenueBusinessInfoFormState,
} from '@/lib/venue/venueBusinessInfoViewModel'
import { toFieldErrorMap } from '@/lib/venue/venueProfileViewModel'
import { useVenueContext } from '@/routes/venueContext'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/** 백엔드 D7-M1 §D7 — 신규 사업자번호가 현행값과 동일(409). */
const BUSINESS_INFO_UNCHANGED_CODE = 40964
/** 백엔드 D7-M1 §D7 — 대기 중 요청이 이미 존재(409). */
const BUSINESS_INFO_ALREADY_REQUESTED_CODE = 40965

const APPROVAL_NOTICE = '제출한 내용은 운영자 승인 후 반영됩니다. 승인 전까지 기존 사업자 정보가 그대로 유지됩니다.'

/**
 * 사업자 정보 수정 요청 폼(#1191, 요구사항 1·2, 백엔드 #1189 §D7-M1).
 *
 * 진입 즉시 M2(현황)로 "대기 중 요청이 있는가"를 판정해 폼/대기 안내 중 하나를 그린다(요구사항 3 —
 * 대기 중 중복 제출 방지의 1차 방어선. 최종 방어선은 서버 409). `VenueOperationVerificationPage.tsx`와
 * 같은 구조를 따르되, 이 폼은 운영 인증 신청과 **별개 축**이라 필드가 사업자등록번호·서류·수정 사유
 * 3개뿐이고 서류가 필수다(백엔드 D4 "전량 교체").
 */
export function VenueBusinessInfoEditPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const infoQuery = useVenueBusinessInfo(venueId)
  const submitMutation = useSubmitVenueBusinessInfoChangeRequest(venueId)

  const [form, setForm] = useState<VenueBusinessInfoFormState>(emptyVenueBusinessInfoFormState())
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState<VenueBusinessInfoView | null>(null)
  const [alreadyRequestedNotice, setAlreadyRequestedNotice] = useState(false)

  if (infoQuery.isLoading) {
    return (
      <>
        <PageHeader title="사업자 정보 수정" />
        <LoadingState rows={4} label="현재 정보를 불러오는 중" />
      </>
    )
  }

  if (infoQuery.isError) {
    return (
      <>
        <PageHeader title="사업자 정보 수정" />
        <ErrorState
          error={infoQuery.error}
          onRetry={() => {
            void infoQuery.refetch()
          }}
        />
      </>
    )
  }

  if (submitted) {
    return <SubmittedNotice venueId={venueId} navigate={navigate} />
  }

  // 🚨 진입 시(직접 URL 접근 포함) 대기 중 요청이 있으면 폼을 열지 않는다(요구사항 3, design.md §2 자율 결정).
  if (infoQuery.data && (hasPendingBusinessInfoChangeRequest(infoQuery.data) || alreadyRequestedNotice)) {
    return <PendingNotice venueId={venueId} navigate={navigate} showAlreadyRequestedNotice={alreadyRequestedNotice} />
  }

  function updateField<K extends keyof VenueBusinessInfoFormState>(key: K, value: VenueBusinessInfoFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const hasUploadingDocument = form.document.status === 'uploading'
  const canSubmit = isVenueBusinessInfoFormComplete(form)

  async function handleSubmit() {
    if (hasUploadingDocument) return

    const clientErrors = validateVenueBusinessInfoForm(form)
    if (clientErrors.length > 0) {
      setFieldErrors(toFieldErrorMap(clientErrors))
      return
    }
    setFieldErrors({})

    try {
      const result = await submitMutation.mutateAsync(toVenueBusinessInfoChangeRequestInput(form))
      setSubmitted(result)
    } catch (error) {
      const apiError = toApiError(error)
      if (apiError.fieldErrors) {
        setFieldErrors(toFieldErrorMap(apiError.fieldErrors))
        return
      }
      if (apiError.code === BUSINESS_INFO_UNCHANGED_CODE) {
        setFieldErrors({ businessNumber: '현재 등록된 사업자등록번호와 동일합니다.' })
        return
      }
      if (apiError.code === BUSINESS_INFO_ALREADY_REQUESTED_CODE) {
        setAlreadyRequestedNotice(true)
      }
    }
  }

  // 필드 오류(400)·409 2종(위에서 각자 처리)은 이미 표시되므로 배너를 중복으로 띄우지 않는다
  // (`VenueOperationVerificationPage.tsx`의 배너 억제 관례와 동일).
  const submitApiError = submitMutation.isError ? toApiError(submitMutation.error) : null
  const submitError =
    submitApiError &&
    !submitApiError.fieldErrors &&
    submitApiError.code !== BUSINESS_INFO_UNCHANGED_CODE &&
    submitApiError.code !== BUSINESS_INFO_ALREADY_REQUESTED_CODE
      ? submitApiError
      : null

  return (
    <>
      <PageHeader title="사업자 정보 수정" description="사업자등록번호와 사업자등록증을 새로 제출합니다." />

      <div className="mb-5 space-y-3">
        <Alert tone="info">{APPROVAL_NOTICE}</Alert>
        {submitError ? <Alert tone="danger">{submitError.message}</Alert> : null}
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader title="새 사업자 정보" />
          <CardBody className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="business-info-number">
                사업자등록번호
              </label>
              <Input
                id="business-info-number"
                value={form.businessNumber}
                aria-invalid={Boolean(fieldErrors.businessNumber)}
                onChange={(event) => {
                  updateField('businessNumber', event.target.value)
                }}
                placeholder="000-00-00000"
              />
              <FieldError message={fieldErrors.businessNumber} />
            </div>

            <OperationVerificationDocumentField
              venueId={venueId}
              document={form.document}
              required
              onChange={(document) => {
                updateField('document', document)
              }}
            />
            <FieldError message={fieldErrors.documentKey} />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="business-info-reason">
                수정 사유 <span className="text-xs font-normal text-slateish-500">(선택)</span>
              </label>
              <Textarea
                id="business-info-reason"
                rows={3}
                value={form.requestReason}
                maxLength={VENUE_BUSINESS_INFO_LIMITS.requestReason}
                aria-invalid={Boolean(fieldErrors.requestReason)}
                onChange={(event) => {
                  updateField('requestReason', event.target.value)
                }}
              />
              <FieldError message={fieldErrors.requestReason} />
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate(-1)
            }}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={submitMutation.isPending || hasUploadingDocument || !canSubmit}
          >
            {submitMutation.isPending ? '제출 중…' : hasUploadingDocument ? '서류 업로드 중…' : '수정 요청 제출'}
          </Button>
        </div>
      </div>
    </>
  )
}

/** 대기 중 요청이 있을 때(진입 시 M2 판정, 또는 제출 중 409) 폼 대신 그리는 안내. */
function PendingNotice({
  venueId,
  navigate,
  showAlreadyRequestedNotice,
}: {
  venueId: string
  navigate: NavigateFunction
  showAlreadyRequestedNotice: boolean
}) {
  return (
    <>
      <PageHeader title="사업자 정보 수정" />
      <div className="space-y-3">
        {showAlreadyRequestedNotice ? (
          <Alert tone="info">이미 접수된 수정 요청이 있습니다. 검토 결과를 기다려 주세요.</Alert>
        ) : null}
        <Card>
          <CardBody className="space-y-2 py-10 text-center">
            <p className="text-base font-semibold text-ink-700">심사 대기 중</p>
            <p className="text-sm text-slateish-600">이미 접수된 수정 요청을 검토하고 있어요. 결과가 나오면 안내해 드려요.</p>
            <div className="pt-3">
              <Button
                variant="secondary"
                onClick={() => {
                  navigate(`/venues/${venueId}/business-info`)
                }}
              >
                사업자 정보로 돌아가기
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

/** 제출 성공 직후 — mutation 응답을 그대로 쓴다(재조회하지 않는다). */
function SubmittedNotice({ venueId, navigate }: { venueId: string; navigate: NavigateFunction }) {
  return (
    <>
      <PageHeader title="사업자 정보 수정" />
      <Card>
        <CardBody className="space-y-2 py-10 text-center">
          <p className="text-base font-semibold text-ink-700">수정 요청이 접수되었습니다</p>
          <p className="text-sm text-slateish-600">{APPROVAL_NOTICE}</p>
          <div className="pt-3">
            <Button
              variant="secondary"
              onClick={() => {
                navigate(`/venues/${venueId}/business-info`)
              }}
            >
              사업자 정보로 돌아가기
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
